<template>
  <div class="container">
    <header>
      <div class="title-banner">
        <h1 class="gb-page-title">Search</h1>
        <p class="title-subheading">Search across Goalbook Toolkit resources, goals, and strategies.</p>
      </div>
    </header>
    <main>
      <ais-instant-search
        v-if="searchClient"
        :search-client="searchClient"
        :index-name="algoliaIndex"
        :middlewares="middlewares">
        <ais-configure :optional-words.camel="optionalQueryWords"/>
        <ais-search-box :search-client="searchClient" :index-name="algoliaIndex">
          <app-debounced-search-box @updatedQuery="onUpdatedQuery" placeholder="Search for a resource, goal, or strategy..."/>
        </ais-search-box>
        <div class="row">
          <div class="col-md-3">
            <div class="well">
              <ais-dynamic-widgets :facets="['*']" :max-values-per-facet=25>
                <ais-panel>
                  <h4 class="filter-header">Types</h4>
                  <ais-hierarchical-menu :attributes="['types.lvl0', 'types.lvl1']" :transform-items="transformItems" />
                </ais-panel>
                <ais-panel>
                  <h4 class="filter-header">Subjects</h4>
                  <ais-menu
                    attribute="subject"
                    :limit=25
                    :transform-items="transformItems"/>
                </ais-panel>
                <ais-panel>
                  <h4 class="filter-header">Grades</h4>
                  <ais-refinement-list
                    attribute="grades"
                    :limit=20
                    :transform-items="transformItems"/>
                </ais-panel>
              </ais-dynamic-widgets>
            </div>
          </div>
          <div class="col-md-9">
            <ais-current-refinements :transform-items="transformRefinementLabels"/>
            <ais-stats/>
            <ais-hits>
              <template v-slot="{ items, sendEvent }">
                <div class="ais-Hits">
                  <ol class="ais-Hits-list">
                    <li
                      class="ais-Hits-item"
                      v-for="item in items"
                      :key="item.objectID">
                      <ResourcePreviewCard
                        v-if="item.content_type === 'Resource'"
                        :key="item.uuid"
                        :uuid="item.uuid"
                        :title="item.title"
                        :description="item.description"
                        :type="item.type"
                        :thumbnail_url="item.thumbnail_url"
                        :grades="item.grades"
                        :subjects="[item.subject] || []"
                        :download_links="item.download_links"
                        :google_drive_links="item.google_drive_links"
                        :aligned-content="item.content_alignments"
                        @resourceDownloadClicked="sendEvent('click', item, 'Resource download clicked')"/>
                      <div class="media" v-else-if="item.content_type === 'Goal' || item.content_type === 'Strategy'">
                        <div class="media-left">
                          <router-link
                            v-if="item.thumbnail_url"
                            :to="getItemUrl(item)"
                            @click.native="sendEvent('click', item, item.content_type + ' clicked')">
                            <img
                              :src="item.thumbnail_url"
                              class="media-object"
                              :alt="item.title">
                          </router-link>
                        </div>
                        <div class="media-body">
                          <h3 class="resource_type text-muted">{{ item.content_type }}</h3>
                          <h2 class="media-heading">
                            <router-link
                              :to="getItemUrl(item)"
                              @click.native="sendEvent('click', item, item.content_type + ' clicked')">
                              {{ item.title }}
                            </router-link>
                          </h2>
                          <AccessPointLabel
                            v-if="item.access_point_level"
                            :level="item.access_point_level"
                            :limit-width="true"
                            class="access-point-label--library"/>
                          <p>{{ item.description }}</p>
                          <p>
                            <small class="text-muted">
                              <template v-if="item.grades">Grade {{ item.grades.join(', ') }}</template>
                              <template v-if="item.grades && item.subject"> · </template>
                              <template v-if="item.subject">{{ item.subject.join(', ') }}</template>
                            </small>
                          </p>
                        </div>
                      </div>
                    </li>
                  </ol>
                </div>
              </template>
            </ais-hits>
            <ais-pagination />
          </div>
        </div>
      </ais-instant-search>
    </main>
    <SubmitFeedback
      type="Sitewide Search"/>
  </div>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import 'instantsearch.css/themes/satellite-min.css';
import { AisStats, AisSortBy, AisDynamicWidgets, AisRefinementList, AisPanel, AisHits, AisCurrentRefinements, AisPagination, AisHierarchicalMenu, AisConfigure } from 'vue-instantsearch';
import AppDebouncedSearchBox from './DebouncedSearchBox.vue'
import SubmitFeedback from '@/components/SubmitFeedback'
import ResourcePreviewCard from '@/components/ResourcePreviewCard'
import AccessPointLabel from '@/components/goals/AccessPointLabel'
import {
  createInsightsMiddleware
} from 'instantsearch.js/es/middlewares';
import aa from 'search-insights';
import { mapGetters } from 'vuex';
import { CreateAlgoliaKeyProviderMixin } from '@/mixins/AlgoliaKeyProvider'

const appID = process.env.VUE_APP_ALGOLIA_APP_ID;
const algoliaIndex = process.env.VUE_APP_ALGOLIA_SITEWIDE_INDEX;

export default {
name: 'SearchPage',
components: {
  AccessPointLabel,
  AppDebouncedSearchBox,
  AisCurrentRefinements,
  AisConfigure,
  AisDynamicWidgets,
  AisHierarchicalMenu,
  AisHits,
  AisPanel,
  AisRefinementList,
  AisStats,
  AisSortBy,
  SubmitFeedback,
  ResourcePreviewCard,
  AisPagination
},
mixins: [CreateAlgoliaKeyProviderMixin('sitewide_search', [algoliaIndex])],
methods: {
  goalUrl(item) {
    return (
      (item.super_page && { name: 'AnchorPage', params: { uuid: item.uuid } }) || {
        name: 'GoalContentPage',
        params: { slug: item.slug }
      }
    )
  },
  getItemUrl(item) {
    if (item.content_type === 'Strategy') {
      return {name: 'StrategyDetailPage', params: {slugOrUuid: item.slug}}
    } else {
      return this.goalUrl(item)
    }
  },
  transformItems(items) {
    // this removes the count so it is not rendered in the HTML
    return items.map(item => ({
      ...item,
      count: undefined,
    }));
  },
  transformRefinementLabels(items) {
    return items.map(item => (
      {
        ...item,
        label: (item.label === 'types.lvl0' || item.label === 'types.lvl1') ? 'Type' : item.label,
      }
    ));
  },
  onUpdatedQuery(newQuery) {
    this.optionalQueryWords = newQuery
  },
  setSearchClient(apiKey) {
    const insightsMiddleware = createInsightsMiddleware({
      insightsClient: aa,
      insightsInitParams: {
        appId: appID,
        apiKey: apiKey,
        useCookie: true // needed so we can generate a userToken for algolia insights events when user is not logged in
      }
    });
    this.middlewares = [insightsMiddleware]
    this.searchClient = algoliasearch(appID, apiKey)
  },
},
computed: {
  ...mapGetters('user', ['userId']),
},
data() {
  return {
    searchClient: null,
    algoliaIndex,
    middlewares: [],
    optionalQueryWords: ''
  };
},
created() {
  const userToken = this.userId || '';
  aa('setUserToken', userToken);
  this.$on('refreshedAlgoliaKey', apiKey => {
    this.setSearchClient(apiKey)
  })
}
};
</script>

<style lang="less" scoped>
@import '~@/variables.less';

.media, .media-body {
  overflow: visible;
}
.media-object {
  max-width: 150px;
}
.ais-RefinementList-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}
.ais-RefinementList-labelText {
  padding-left: 4px;
}
.media-heading {
  font-size: 18px;
    text-transform: none;
    font-weight: bold;
    line-height: 24px;
    margin-top: 0;
}
.resource_type {
  margin-top: 0;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 500;
}
.filter-header {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #555;
  margin: 0 0 5px 0;
  padding: 3px 0;
  text-transform: uppercase;
}
.ais-Stats {
  padding-bottom: 16px;
}
.ais-CurrentRefinements-list {
  padding-bottom: 10px;
}
.ais-SearchBox {
  padding-bottom: 20px;
}
.ais-Menu-count, .ais-RefinementList-count, .ais-HierarchicalMenu-count {
  display: none;
}
// hide the arrows for the hierarchicalMenu expand/collapse
.ais-HierarchicalMenu-link::before {
  background: none;
  margin-right: 0;
  width: 0;
}
</style>
